import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toastHelpers } from '@/lib/toast-helpers'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useCategories } from '@/hooks/useCategories'
import { cn } from '@/lib/utils'
import { choiceFromOption, draftFromGroup, optionsPayload, type VariantDraft } from '@/lib/variantShape'
import {
  cellState,
  draftFromAttachments,
  groupProducts,
  isOn,
  matrixColumns,
  planSave,
  sizeGroupFor,
  toggleCategory,
  toggleCell,
  type MatrixDraft,
} from '@/lib/variantMatrix'
import type { ModifierCategoryPriceImpact, Product } from '@/types'
import { PriceImpactDialog } from '@/components/forms/CategoryModifierSection'

/**
 * The menu matrix (F-MOD-N12): items down, shared lists across.
 *
 * Tick a cell to ask that list on that item; tick the category row to ask it
 * on every item in the category, including ones added later. An item can opt
 * out of its category's list. Size prices are typed in place.
 *
 * Saving writes only what changed, categories first. A category attachment
 * that moves money comes back as a 409 with a per-item preview and nothing
 * written; the same PriceImpactDialog the category form uses shows it, and
 * only a confirmed re-send writes. That round trip is the safeguard.
 */

async function fetchAllAdminProducts(): Promise<Product[]> {
  const out: Product[] = []
  for (let page = 1; page <= 50; page += 1) {
    const res = await apiClient.getAdminProducts({ page, per_page: 100 })
    if (!res.success) throw new Error(res.message)
    const rows = res.data ?? []
    out.push(...rows)
    const meta = (res as { meta?: { total_pages?: number } }).meta
    if (rows.length < 100 || (meta?.total_pages != null && page >= meta.total_pages)) break
  }
  return out
}

export function VariantMatrix() {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()

  const { data: categories = [] } = useCategories()
  const { data: products = [], isLoading: productsLoading } = useQuery({ queryKey: ['variant-matrix-products'], queryFn: fetchAllAdminProducts })
  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ['modifier-groups'],
    queryFn: async () => { const r = await apiClient.getModifierGroups(); if (!r.success) throw new Error(r.message); return r.data ?? [] },
  })
  const { data: attachments, isLoading: attLoading } = useQuery({
    queryKey: ['modifier-attachments'],
    queryFn: async () => { const r = await apiClient.getModifierAttachments(); if (!r.success) throw new Error(r.message); return r.data },
  })

  const base = useMemo(() => draftFromAttachments(attachments), [attachments])
  const [draft, setDraft] = useState<MatrixDraft>(base)
  const [sizeEdits, setSizeEdits] = useState<Record<string, VariantDraft>>({})
  useEffect(() => { setDraft(base); setSizeEdits({}) }, [base])

  const byId = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups])
  const columns = useMemo(() => matrixColumns(groups, attachments), [groups, attachments])
  const rows = useMemo(() => groupProducts(products, categories), [products, categories])

  const plan = useMemo(() => planSave(base, draft), [base, draft])
  const sizeChanges = Object.keys(sizeEdits).length
  const changeCount = plan.categories.length + plan.products.length + sizeChanges
  const movesMoney = plan.categories.some((c) => c.groupIds.some((id) => { const g = byId.get(id); return g && (g.price_mode === 'replace' || g.min_select >= 1) }))

  // ── size price editing ──────────────────────────────────────────────────
  const sizeDraftFor = (groupId: string): VariantDraft | null => {
    if (sizeEdits[groupId]) return sizeEdits[groupId]
    const g = byId.get(groupId)
    return g ? draftFromGroup(g) : null
  }
  const setSizePrice = (groupId: string, optionIndex: number, price: number) => {
    const d = sizeDraftFor(groupId)
    if (!d) return
    const g = byId.get(groupId)
    const fresh = g ? { ...d, choices: d.choices.length ? d.choices : (g.options ?? []).map(choiceFromOption) } : d
    setSizeEdits((prev) => ({
      ...prev,
      [groupId]: { ...fresh, choices: fresh.choices.map((c, i) => (i === optionIndex ? { ...c, price } : c)) },
    }))
  }

  // ── save ────────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false)
  const [pending, setPending] = useState<{ categoryId: string; groupIds: string[]; impact: ModifierCategoryPriceImpact; rest: number } | null>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['modifier-attachments'] })
    qc.invalidateQueries({ queryKey: ['modifier-groups'] })
    qc.invalidateQueries({ queryKey: ['product-modifier-groups'] })
    qc.invalidateQueries({ queryKey: ['category-modifier-groups'] })
    qc.invalidateQueries({ queryKey: ['variant-usage'] })
    qc.invalidateQueries({ queryKey: ['menu-modifiers'] })
  }

  /** Walk the plan from `from`; stop at the first category that needs a confirm. */
  const runSave = async (from: number, confirmCategory?: string) => {
    setSaving(true)
    try {
      for (let i = from; i < plan.categories.length; i += 1) {
        const c = plan.categories[i]
        const res = await apiClient.setCategoryModifierGroups(c.id, c.groupIds, confirmCategory === c.id)
        if (res.outcome === 'needs_confirmation') {
          // Stop here and ask; the dialog's confirm resumes from this index.
          setPending({ categoryId: c.id, groupIds: c.groupIds, impact: res.impact, rest: i })
          setSaving(false)
          return
        }
      }
      for (const p of plan.products) {
        const res = await apiClient.setProductModifierGroups(p.id, { group_ids: p.direct, excluded_category_group_ids: p.excluded })
        if (!res.success) throw new Error(res.message || 'Could not save an item')
      }
      for (const [gid, d] of Object.entries(sizeEdits)) {
        const res = await apiClient.replaceModifierOptions(gid, optionsPayload(d))
        if (!res.success) throw new Error(res.message || 'Could not save size prices')
      }
      toastHelpers.success('Matrix saved', `${changeCount} change${changeCount === 1 ? '' : 's'} applied.`)
      invalidate()
      setPending(null)
      setSaving(false)
    } catch (e) {
      toastHelpers.error('Could not save the matrix', e instanceof Error ? e.message : 'Unknown error')
      invalidate()
      setPending(null)
      setSaving(false)
    }
  }

  if (productsLoading || groupsLoading || attLoading) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Loading the menu…</p>
  }
  if (columns.length === 0) {
    return (
      <p className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
        No shared lists yet. Add one under Shared lists, or open an item and type a question there.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/60 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="sticky left-0 z-10 bg-muted/60 px-3 py-2 text-left">Item</th>
              <th className="px-2 py-2 text-right">Price</th>
              {columns.map((g) => (
                <th key={g.id} className="px-2 py-2 text-center" title={g.prompt}>
                  {g.name}
                  {g.price_mode === 'replace' && <span className="block text-[9px] font-normal normal-case tracking-normal">sets price</span>}
                </th>
              ))}
              <th className="px-2 py-2 text-left">Sizes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ category, products: items }) => (
              <RowGroup key={category?.id ?? 'none'}>
                <tr className="border-t bg-muted/30">
                  <th className="sticky left-0 z-10 bg-muted/30 px-3 py-1.5 text-left text-xs font-semibold" scope="rowgroup">
                    {category?.name ?? 'No category'}
                    <span className="ml-1 font-normal text-muted-foreground">· {items.length} item{items.length === 1 ? '' : 's'}</span>
                  </th>
                  <td className="px-2 py-1.5 text-right text-[11px] text-muted-foreground">category</td>
                  {columns.map((g) => {
                    const on = Boolean(category && draft.categories[category.id]?.includes(g.id))
                    return (
                      <td key={g.id} className="px-2 py-1.5 text-center">
                        {category && (
                          <Cell on={on} kind={on ? 'direct' : 'off'} label={`${g.name} on every item in ${category.name}`} onClick={() => setDraft((d) => toggleCategory(d, category.id, g.id))} />
                        )}
                      </td>
                    )
                  })}
                  <td className="px-2 py-1.5 text-[11px] text-muted-foreground">new items inherit</td>
                </tr>
                {items.map((p) => {
                  const size = sizeGroupFor(draft, p, byId)
                  const sd = size ? sizeDraftFor(size.id) : null
                  return (
                    <tr key={p.id} className="border-t">
                      <td className="sticky left-0 z-10 bg-background px-3 py-1">
                        <span className={cn(!p.is_available && 'text-muted-foreground line-through')}>{p.name}</span>
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                        {size ? <span className="text-[11px]">by size</span> : formatCurrency(p.price)}
                      </td>
                      {columns.map((g) => {
                        const st = cellState(draft, p.id, p.category_id, g.id)
                        return (
                          <td key={g.id} className="px-2 py-1 text-center">
                            <Cell on={isOn(st)} kind={st} label={`${g.name} on ${p.name}`} onClick={() => setDraft((d) => toggleCell(d, p.id, p.category_id, g.id))} />
                          </td>
                        )
                      })}
                      <td className="px-2 py-1">
                        {size && sd ? (
                          <span className="flex flex-wrap items-center gap-1.5 text-xs">
                            {sd.choices.map((c, i) => (
                              <span key={c.id ?? i} className="inline-flex items-center gap-1">
                                <span className="text-muted-foreground">{c.name}</span>
                                <Input
                                  type="number"
                                  min={0}
                                  step="any"
                                  inputMode="decimal"
                                  aria-label={`${p.name} ${c.name} price`}
                                  className="h-7 w-20 px-1.5 text-right text-xs tabular-nums"
                                  value={c.price}
                                  onChange={(e) => setSizePrice(size.id, i, Number(e.target.value) || 0)}
                                />
                              </span>
                            ))}
                            {(size.options?.length ?? 0) > 0 && columns.some((c) => c.id === size.id) && (
                              <span className="text-[10px] text-muted-foreground">shared</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </RowGroup>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <div className="flex flex-wrap gap-4">
          <span className="inline-flex items-center gap-1.5"><Cell on kind="direct" label="" /> on this item</span>
          <span className="inline-flex items-center gap-1.5"><Cell on kind="inherited" label="" /> on via its category</span>
          <span className="inline-flex items-center gap-1.5"><Cell on={false} kind="excluded" label="" /> opted out</span>
          <span className="inline-flex items-center gap-1.5"><Cell on={false} kind="off" label="" /> off</span>
        </div>
        <div className="flex items-center gap-3">
          <span>
            {changeCount === 0
              ? 'No changes'
              : `${changeCount} change${changeCount === 1 ? '' : 's'} unsaved${movesMoney ? ' · a category change moves prices, you will be shown what' : ''}`}
          </span>
          <Button size="sm" variant="outline" disabled={changeCount === 0 || saving} onClick={() => { setDraft(base); setSizeEdits({}) }}>Discard</Button>
          <Button size="sm" disabled={changeCount === 0 || saving} onClick={() => runSave(0)}>{saving ? 'Saving…' : 'Save matrix'}</Button>
        </div>
      </div>

      {pending && (
        <PriceImpactDialog
          impact={pending.impact}
          saving={saving}
          onCancel={() => { setPending(null); setSaving(false) }}
          onConfirm={() => runSave(pending.rest, pending.categoryId)}
        />
      )}
    </div>
  )
}

function RowGroup({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

function Cell({ on, kind, label, onClick }: { on: boolean; kind: 'direct' | 'inherited' | 'excluded' | 'off'; label: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      aria-label={label || undefined}
      tabIndex={onClick ? 0 : -1}
      onClick={onClick}
      className={cn(
        'inline-grid h-[18px] w-[18px] place-items-center rounded-[5px] border text-[11px] transition-colors',
        kind === 'direct' && 'border-primary bg-primary text-primary-foreground',
        kind === 'inherited' && 'border-primary bg-primary/15 text-primary',
        kind === 'excluded' && 'border-dashed border-amber-500 bg-transparent',
        kind === 'off' && 'border-input bg-background',
        onClick && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      {on && <Check className="h-3 w-3" />}
    </button>
  )
}
